import React, { useState, useEffect } from "react";
import { Spinner } from "react-bootstrap";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import MapRouting from "./MapRouting"; // Uses your existing leafet-routing-machine wrapper

interface BookingRoutePreviewMapProps {
  businessLocation: { lat: number; lng: number; name: string };
  customerLocation: { lat: number; lng: number; address: string };
  onDistanceCalculated?: (distanceStr: string) => void;
}

export default function BookingRoutePreviewMap({
  businessLocation,
  customerLocation,
  onDistanceCalculated
}: BookingRoutePreviewMapProps) {
  const [mapIsReady, setMapIsReady] = useState(false);

  const centerPosition: [number, number] = [
    (businessLocation.lat + customerLocation.lat) / 2,
    (businessLocation.lng + customerLocation.lng) / 2,
  ];

  // Run the precise mathematical Haversine check for UI consistency
  useEffect(() => {
    if (businessLocation.lat && businessLocation.lng && customerLocation.lat && customerLocation.lng) {
      const R = 6371; // Earth radius in km
      const dLat = ((customerLocation.lat - businessLocation.lat) * Math.PI) / 180;
      const dLon = ((customerLocation.lng - businessLocation.lng) * Math.PI) / 180;

      const a =
        Math.sin(dLat / 2) ** 2 +
        Math.cos((businessLocation.lat * Math.PI) / 180) *
          Math.cos((customerLocation.lat * Math.PI) / 180) *
          Math.sin(dLon / 2) ** 2;

      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      const distance = R * c;

      const formattedDistance = distance < 1 
        ? `${(distance * 1000).toFixed(0)} meters` 
        : `${distance.toFixed(2)} km`;

      if (onDistanceCalculated) {
        onDistanceCalculated(formattedDistance);
      }
    }
  }, [businessLocation, customerLocation, onDistanceCalculated]);

  return (
    <div className="position-relative w-100 rounded overflow-hidden border" style={{ height: "320px" }}>
      
      {/* MAP LOADING OVERLAY */}
      {!mapIsReady && (
        <div style={{
          position: "absolute", top: 0, left: 0, right: 0, bottom: 0,
          zIndex: 1000, display: "flex", flexDirection: "column",
          justifyContent: "center", alignItems: "center", background: "#f8f9fa"
        }}>
          <Spinner animation="border" variant="primary" size="sm" />
          <small className="mt-2 text-muted fw-medium">Loading route workspace...</small>
        </div>
      )}

      {/* CORE LEAFLET FRAME CONTAINER */}
      <MapContainer
        center={centerPosition}
        zoom={12}
        scrollWheelZoom={true}
        style={{ height: "100%", width: "100%" }}
        whenReady={() => setTimeout(() => setMapIsReady(true), 400)}
      >
        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />

        {/* Marker 1: Business Center (Start Point) */}
        <Marker position={[businessLocation.lat, businessLocation.lng]}>
          <Popup>
            <div className="small">
              <strong className="text-primary">{businessLocation.name}</strong><br />
              Starting Hub Location
            </div>
          </Popup>
        </Marker>

        {/* Marker 2: Customer House (End Point) */}
        <Marker position={[customerLocation.lat, customerLocation.lng]}>
          <Popup>
            <div className="small">
              <strong>Customer Destination</strong><br />
              {customerLocation.address || "No street address typed."}
            </div>
          </Popup>
        </Marker>

        {/* Dynamic Route Polyline Vector Engine */}
        <MapRouting
          userLocation={{ lat: businessLocation.lat, lon: businessLocation.lng }}
          targetLocation={{ lat: customerLocation.lat, lon: customerLocation.lng }}
        />
      </MapContainer>
    </div>
  );
}